import { enrollMembership, type EnrollmentRequest } from '@/lib/memberships-api-client';

export type OfflineEnrollmentQueueItem = {
  id: string;
  created_at: string;
  request: EnrollmentRequest;
};

export type ProcessOfflineEnrollmentQueueResult = {
  processed: number;
  remaining: number;
  error: string | null;
};

const DB_NAME = 'rahsmatch_tech_portal';
const DB_VERSION = 1;
const STORE_NAME = 'offline_enrollment_queue';

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

function isIndexedDbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function createQueueId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionToPromise(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

async function openDb(): Promise<IDBDatabase> {
  if (!isIndexedDbAvailable()) {
    throw new Error('IndexedDB is unavailable in this browser.');
  }

  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('created_at', 'created_at');
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function isLikelyOfflineError(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;

  const msg = toErrorMessage(error).toLowerCase();
  return msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('load failed');
}

export async function enqueueOfflineEnrollment(request: EnrollmentRequest): Promise<OfflineEnrollmentQueueItem> {
  const item: OfflineEnrollmentQueueItem = {
    id: createQueueId(),
    created_at: new Date().toISOString(),
    request,
  };

  const db = await openDb();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    store.put(item);

    await transactionToPromise(tx);
    return item;
  } finally {
    db.close();
  }
}

export async function getOfflineEnrollmentQueueCount(): Promise<number> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const countReq = store.count();
    const count = await requestToPromise(countReq);
    await transactionToPromise(tx);
    return Number(count || 0);
  } finally {
    db.close();
  }
}

export async function listOfflineEnrollmentQueue(): Promise<OfflineEnrollmentQueueItem[]> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);

    const items: OfflineEnrollmentQueueItem[] = [];

    await new Promise<void>((resolve, reject) => {
      const cursorReq = store.index('created_at').openCursor();
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (!cursor) {
          resolve();
          return;
        }

        items.push(cursor.value as OfflineEnrollmentQueueItem);
        cursor.continue();
      };
      cursorReq.onerror = () => reject(cursorReq.error);
    });

    await transactionToPromise(tx);
    return items;
  } finally {
    db.close();
  }
}

export async function removeOfflineEnrollment(id: string): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    store.delete(id);

    await transactionToPromise(tx);
  } finally {
    db.close();
  }
}

export async function processOfflineEnrollmentQueue(): Promise<ProcessOfflineEnrollmentQueueResult> {
  const queue = await listOfflineEnrollmentQueue();

  let processed = 0;

  for (const item of queue) {
    try {
      await enrollMembership(item.request);
      await removeOfflineEnrollment(item.id);
      processed += 1;
    } catch (error) {
      const remaining = await getOfflineEnrollmentQueueCount();
      return {
        processed,
        remaining,
        error: toErrorMessage(error),
      };
    }
  }

  const remaining = await getOfflineEnrollmentQueueCount();

  return {
    processed,
    remaining,
    error: null,
  };
}
